import {ArrowRightIcon, ChevronDownIcon, ChevronRightIcon, FoldIcon, UnfoldIcon} from '@primer/octicons-react'
import {Box, IconButton, Link, Octicon} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import type {PatchStatus} from '@github-ui/diff-file-helpers'
import {fileModeChangedOnly} from '@github-ui/diff-file-helpers'
import type {MouseEvent, ReactNode} from 'react'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard'
import {LinesChangedCounterLabel} from './LinesChangedCounterLabel'

function FileName({
  newPath,
  oldPath,
  status,
}: {
  newPath?: string | null
  oldPath?: string | null
  status: PatchStatus
}): JSX.Element {
  if (status === 'RENAMED') {
    return (
      <code>
        {oldPath} <ArrowRightIcon /> {newPath}
      </code>
    )
  } else if (status === 'DELETED') {
    return <code>{oldPath}</code>
  } else {
    return <code>{newPath}</code>
  }
}

export interface DiffFileHeaderProps {
  /**
   * Optional additional content to render to the left of the file name
   */
  additionalLeftSideContent?: ReactNode
  /**
   * Indicates whether the lines in the file have all been expanded
   */
  areLinesExpanded: boolean
  /**
   * Indicates whether the "expand all" button should be shown
   */
  canExpandOrCollapseLines: boolean
  /**
   * Indicates whether the file is in a collapsed state
   */
  isCollapsed: boolean
  /**
   * Count of the number of lines added to the file
   */
  linesAdded: number
  /**
   * Count of the number of lines deleted from the file
   */
  linesDeleted: number
  /**
   * The href to use for the file name link
   */
  fileLinkHref?: string
  /**
   * The new git mode of the file
   */
  newMode?: number
  /**
   * The new path of the file
   */
  newPath?: string | null
  /**
   * The old git mode of the file
   */
  oldMode?: number
  /**
   * The old path of the file
   */
  oldPath?: string | null
  /**
   * Callback invoked when the user clicks the button to copy the file path
   */
  onCopyPath?: () => void
  /**
   * Callback invoked when the user clicks the header
   */
  onHeaderClick?: (e: MouseEvent<HTMLElement>) => void
  /**
   * Callback invoked when the user clicks the button to toggle the expanded state of all lines
   */
  onToggleExpandAllLines?: () => void
  /**
   * Callback invoked when the user clicks the button to toggle the collapsed state of the file
   */
  onToggleFileCollapsed: () => void
  /**
   * The status of the file
   */
  patchStatus: PatchStatus
  /**
   * The path of the file
   */
  path: string
  /**
   * Optional additional content to render after the file name. This content will be right-aligned.
   */
  rightSideContent?: ReactNode
}

/**
 * Renders the header of a diff file.
 */
export function DiffFileHeader({
  additionalLeftSideContent,
  areLinesExpanded,
  canExpandOrCollapseLines,
  isCollapsed,
  linesAdded,
  linesDeleted,
  fileLinkHref,
  newMode,
  newPath,
  oldMode,
  oldPath,
  onCopyPath,
  onHeaderClick,
  onToggleExpandAllLines,
  onToggleFileCollapsed,
  patchStatus,
  path,
  rightSideContent,
}: DiffFileHeaderProps) {
  return (
    <Box
      sx={{
        position: 'relative',
        alignItems: 'center',
        bg: 'canvas.subtle',
        border: '1px solid',
        borderColor: 'border.default',
        borderRadius: 2,
        borderBottomLeftRadius: isCollapsed ? 2 : 0,
        borderBottomRightRadius: isCollapsed ? 2 : 0,
        display: 'flex',
        p: 2,
        mb: '-1px',
      }}
    >
      <IconButton
        aria-label={isCollapsed ? `expand file: ${path}` : `collapse file: ${path}`}
        icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
        variant="invisible"
        onClick={onToggleFileCollapsed}
        sx={{flexShrink: 0}}
      />
      {additionalLeftSideContent}
      <Box
        sx={{
          pl: 1,
          pr: 1,
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          as="h3"
          sx={{
            fontSize: 1,
            fontWeight: 'unset',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            direction: 'rtl',
          }}
        >
          <Link className="Link--primary" href={fileLinkHref} onClick={onHeaderClick}>
            <FileName newPath={newPath} oldPath={oldPath} status={patchStatus} />
          </Link>
        </Box>
        <CopyToClipboardButton
          sx={{ml: 1, flexShrink: 0}}
          textToCopy={newPath ?? oldPath ?? ''}
          ariaLabel="Copy file name to clipboard"
          tooltipProps={{direction: 's'}}
          onCopy={onCopyPath}
        />
        {fileModeChangedOnly(patchStatus, oldMode, newMode) && (
          <Box sx={{p: 2}}>
            <code>{oldMode}</code>
            <Octicon icon={ArrowRightIcon} sx={{mx: 1}} />
            <code>{newMode}</code>
          </Box>
        )}
        {canExpandOrCollapseLines && onToggleExpandAllLines && (
          <Tooltip
            text={areLinesExpanded ? `collapse non diff lines: ${path}` : `expand all lines: ${path}`}
            direction="s"
          >
            <IconButton
              aria-label={areLinesExpanded ? `collapse non diff lines: ${path}` : `expand all lines: ${path}`}
              icon={areLinesExpanded ? FoldIcon : UnfoldIcon}
              variant="invisible"
              className={areLinesExpanded ? '' : `js-expand-all-difflines-button`}
              data-file-path={path}
              onClick={onToggleExpandAllLines}
              sx={{flexShrink: 0}}
            />
          </Tooltip>
        )}
      </Box>
      <Box sx={{display: 'flex', flexGrow: 1, justifyContent: 'flex-end', mr: 2}}>
        {linesAdded > 0 && <LinesChangedCounterLabel isAddition>+{linesAdded}</LinesChangedCounterLabel>}
        {linesDeleted > 0 && <LinesChangedCounterLabel isAddition={false}>-{linesDeleted}</LinesChangedCounterLabel>}
      </Box>
      {rightSideContent}
    </Box>
  )
}

try{ FileName.displayName ||= 'FileName' } catch {}
try{ DiffFileHeader.displayName ||= 'DiffFileHeader' } catch {}